import { Controller } from '@hotwired/stimulus';

// "Customize Goals Shown" on a progress report's fill page. The picker's checkboxes are fields of
// the report's own form (progress_report[presentation][show_goals][<goal id>]), so the choice saves
// with the report; here they work the page in the meantime: a checked goal's card shows, an
// unchecked one's hides, unchecking a long-term goal unchecks its short-term goals, and checking a
// short-term goal checks its long-term goal (a short-term goal cannot stand without it). The
// hidden show_selected_goals field turns on with the first change, and Reset to defaults turns it
// off and puts every box back to the default rule (data-default on each box, the open goals).
//
// Targets:
//   box    a goal's checkbox (data-goal, data-parent for a short-term goal, data-default)
//   chosen the hidden show_selected_goals field ('1' while the choice is a choice)
export default class extends Controller {
  static targets = ['box', 'chosen'];

  change(event) {
    const box = event.target;
    if (!box.checked && !box.dataset.parent) {
      // A long-term goal unchecked takes its short-term goals with it.
      this.boxTargets.forEach((other) => {
        if (other.dataset.parent === box.dataset.goal) other.checked = false;
      });
    }
    if (box.checked && box.dataset.parent) {
      // A short-term goal checked brings its long-term goal with it.
      this.boxTargets.forEach((other) => {
        if (other.dataset.goal === box.dataset.parent) other.checked = true;
      });
    }
    this.markChosen(true);
    this.apply();
  }

  reset() {
    this.boxTargets.forEach((box) => {
      box.checked = box.dataset.default === 'true';
    });
    this.markChosen(false);
    this.apply();
  }

  // Every card as the boxes say: a goal shows while its box is checked. A goal the last save
  // flagged (data-goals-report-flagged) stays on show whatever its box says, the way the server drew
  // it: the message tells the therapist to show it or clear the change, and clearing it needs the row.
  // So does a goal the report itself adds (data-goals-report-added): the picker has no box for it,
  // and the report's own doing is never hidden from it.
  apply() {
    const checked = new Set(this.boxTargets.filter((box) => box.checked).map((box) => box.dataset.goal));
    this.element.querySelectorAll('[data-goals-report-goal]').forEach((card) => {
      const kept = card.dataset.goalsReportFlagged === 'true' || 'goalsReportAdded' in card.dataset;
      card.hidden = !checked.has(card.dataset.goalsReportGoal) && !kept;
    });
    // A goal off the report carries no comment editor: the page renders every goal of the
    // discipline for this picker, and mounting an editor for each at load is more than the biggest
    // patients can carry. A goal shown here gets its editor now.
    if (typeof window.mountVisibleCkeditors === 'function') window.mountVisibleCkeditors();
    this.dispatch('changed');
  }

  markChosen(chosen) {
    this.chosenTargets.forEach((field) => {
      field.value = chosen ? '1' : '0';
    });
  }
}
